/* eslint-disable @typescript-eslint/no-explicit-any */
import i18n from "@/i18n";
import { DateTime } from "luxon";
import { isValidPhoneNumber } from "libphonenumber-js";
import { validateIban } from "../../api/iban";

import {
  validateStreetNumber,
  validatePhone,
  validatePostalcode,
} from "@/changeservice/components/ChangeServiceEdit/validations";
import {
  isValueNumber,
  toDate,
  isPasswordFormat,
  isMaxLength as isValueMaxLength,
  isMinLength as isValueMinLength,
  isBic,
  isPhone as isValuePhone,
} from "../utils/utils";
import { IbanRespone } from "@/types/types";
import { UserT } from "@/auth/components/MyProfile/misc/Types";
import { PasswordDetailsT } from "@/changeservice/components/MissingData/types";
import { isEmpty } from "@/utils/utils";
import { isValid } from "iban";

export const required = (value?: unknown) => (value ? undefined : i18n.t("validations.required"));
export const requiredDOB = (value?: unknown) => (value ? undefined : i18n.t("validations.requiredDOB"));

export const isLength = (value: string, length: number) => {
  if (value.length !== length) {
    return i18n.t("validations.invalidLength", { length });
  }
  return undefined;
};

export const validateUser = ({
  firstname,
  lastname,
  postalcode,
  streetNumber,
  landlinePhoneNumber,
  cellPhoneNumber,
  dateOfBirth,
  accountHolder,
  accountIban,
  accountBic,
  accountNumber,
  accountBankNumber,
}: UserT): string => {
  const validations = [
    { field: "firstname", error: validateName(firstname ?? "") },
    { field: "lastname", error: validateName(lastname ?? "") },
    { field: "postalcode", error: validatePostalcode(postalcode) },
    { field: "streetNumber", error: validateStreetNumber(streetNumber) },
    { field: "landlinePhoneNumber", error: validatePhone(landlinePhoneNumber) },
    { field: "cellPhoneNumber", error: validatePhone(cellPhoneNumber) },
    { field: "dateOfBirth", error: validateDateOfBirth(dateOfBirth) },
    { field: "accountHolder", error: validateName(accountHolder ?? "") },
    { field: "accountIban", error: validateIbanSync(accountIban ?? "") },
    { field: "accountBic", error: validateBic(accountBic) },
    { field: "accountNumber", error: validateAccountNumber(accountNumber) },
    { field: "accountBankNumber", error: validateAccountBankNumber(accountBankNumber) },
  ];

  const failedValidation = validations.find((v) => v.error);
  if (failedValidation) {
    console.log(`Validation failed for ${failedValidation.field}:`, failedValidation.error);
    return failedValidation.error;
  }
  return "";
};

export const addAreaCode = (phoneNumber?: string) => (phoneNumber ? `+49${removeAreaCode(phoneNumber)}` : undefined);

export const removeAreaCode = (phoneNumber?: string) =>
  phoneNumber?.replace(/[^\d]/g, "").replace(/^49/g, "").replace(/^0*/g, "");

export const validateDateOfBirth = (date?: any) => date && evaluate(date)([isNewDate, isNew18orOlder]);

export function isMaxLength(maxLength: number) {
  return function (value: string) {
    return isValueMaxLength(maxLength)(value) ? undefined : i18n.t("validation.isMaxLength", { maxLength });
  };
}
// eslint-disable-next-line
export const isNew18orOlder = (value: any) => {
  const date18yearsAgo = new Date(new Date().setFullYear(new Date().getFullYear() - 18));
  const dateValue = value && new Date(value);

  return date18yearsAgo < dateValue
    ? !isEmpty(value)
      ? "Sie müssen mindestens 18 Jahre alt sein, um unseren Service nutzen zu können."
      : undefined
    : undefined;
};

export function isMinLength(minLength: number) {
  return function (value: string) {
    return isValueMinLength(minLength)(value) ? undefined : i18n.t("validation.isMinLength", { minLength });
  };
}

export const lengthIsLessThan = (maxLength: number) => (value?: unknown) => {
  if (value && typeof value === "string" && value.length > maxLength) {
    return i18n.t("validation.lengthIsLessThan", { maxLength });
  } else return undefined;
};
export function removeChars(remove = ".-") {
  return function (toBeStripped: string) {
    const regexp = new RegExp(`[${remove}]+`, "g");
    return toBeStripped.replace(regexp, "");
  };
}
export function minLength2pruned(text: string) {
  return isMinLength(2)(removeChars()(text));
}

export function isPhone(value: string) {
  return isValuePhone(value) ? undefined : i18n.t("validation.isPhone");
}
export const validateE164Phone = (number?: string) => {
  return number ? evaluate(number)([isPhoneNumberValid]) : undefined;
};
// eslint-disable-next-line
export const acceptDigitsAndSpaces = (event: any) => {
  if (!/[0-9 ]+/.test(event.key)) {
    event.preventDefault();
  }
};
//! WE SHOULD RETHINK THIS NAMING BECAUSE THE NAMING IS DIFFERENT TO WHAT THE FUNCTIONS DOES
//! EXAMPLE:
//! I would expect that lengthIsMoreThan(1)("a") is returning an error, because the length of the given string is 1 and not more than 1. But it is not returning an error, because it is just checking, if the given string length is less then the given number. So the function name is missleading.
export const lengthIsMoreThan = (minLength: number) => (value?: unknown) => {
  if (value && typeof value === "string" && value.length < minLength) {
    return i18n.t("validation.lengthIsMoreThan", { minLength });
  } else return undefined;
};
const evaluate = (value: string) => (validators: Array<(val: string) => string | boolean | undefined>) => {
  return validators
    .map((validator) => {
      const res = validator(value);
      return res;
    })
    .find((error) => error);
};

export const noSpecialChars = (value = "") => {
  const errorMessage = i18n.t("validation.noSpecialChars");
  const charsToAvoid = '1234567890!@#$%^&*\\[\\]()_?+={};:"|,<>/';
  const regExpression = new RegExp(`[${charsToAvoid}]`);
  return regExpression.test(value) ? errorMessage : undefined;
};

export const composeValidators =
  (...validators: any) =>
  // eslint-disable-next-line
  (value: any) =>
    // eslint-disable-next-line
    validators.reduce((error: any, validator: any) => error || validator(value), undefined);
// eslint-disable-next-line
export const isRequired = (value: any) => {
  const isNumber = typeof value === "number";
  return !isNumber ? (isEmpty(value) ? i18n.t("validation.isPresent") : undefined) : undefined;
};
export const nameValidator = composeValidators(isRequired, noSpecialChars, minLength2pruned);

export function isSingleName(value: string) {
  const conjunctions = [" and ", " und ", " & "];
  const result = conjunctions.filter((conjunction: string) => {
    return value?.toLowerCase()?.includes(conjunction);
  });
  return result.length ? i18n.t("validation.isSingleName") : undefined;
}

export const numbersOnly = (value?: unknown) => {
  return (value && typeof value === "number") || (typeof value === "string" && /^[0-9]*$/.test(value))
    ? undefined
    : i18n.t("validations.isNumber");
};

export const isInteger = (value: number | string) =>
  Number.isInteger(value) || (typeof value === "string" && /^(\d+(?![.\d]))$/.test(value))
    ? undefined
    : i18n.t("validations.isInteger");

export const isNumberSmallerThan = (maxNumber: number) => {
  return (value: unknown) => {
    return Number(value) <= maxNumber ? undefined : i18n.t("validations.isNumberTooHigh");
  };
};

export const validateName = (name: string) => {
  return evaluate(name)([lengthIsMoreThan(2), lengthIsLessThan(100), () => noSpecialChars(name)]);
};

export const atLeastOneDigit = (value?: string) => {
  return value && !/\d/.test(value) ? i18n.t("validations.atLeastOneDigit") : undefined;
};

// Fields
export function isIban(value?: string) {
  if (!value) {
    return undefined;
  }

  if (value.slice(0, 2) !== "DE") return i18n.t("validation.isIban");

  if (isValid(value)) {
    return undefined;
  }

  return i18n.t("validation.isIban");
}

export const validateIbanSync = (number?: string) => evaluate(number ?? "")([isIban]);

export const isEmail = (value?: unknown) => {
  return typeof value !== "string" || /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,63}$/i.test(value)
    ? undefined
    : i18n.t("validations.isEmail");
};

export const isPassword = (value: unknown) => {
  if (typeof value !== "string") {
    return undefined;
  }
  const regX = /^[a-zA-ZäöüÄÖÜß0-9!@#$%^&*()_+\-=\[\]{};:'",.<>\/\\?]{12,}$/;
  return !value || regX.test(value) ? undefined : i18n.t("validations.isPassword");
};

export const isConfirmedPassword = (password: string) => {
  return (value: unknown) => {
    return value === password ? undefined : i18n.t("validations.isConfirmedPassword");
  };
};
export const isNumber = function (value: number | string) {
  return isValueNumber(value) ? undefined : i18n.t("validations.isNumber");
};

export const validateBic = (number?: string) => number && evaluate(number)(isBic);
export const validateAccountNumber = (number?: string) => {
  if (!number) return undefined;

  // Must be a number
  if (!isValueNumber(number)) {
    return i18n.t("validations.isNumber");
  }

  // Must be between 1 and 10 digits
  if (number.length < 1 || number.length > 10) {
    return i18n.t("validations.invalidAccountNumber");
  }

  return undefined;
};
export const validateAccountBankNumber = (number?: string) => {
  if (!number) return undefined;

  // Must be a number
  if (!isValueNumber(number)) {
    return i18n.t("validations.isNumber");
  }

  // Must be exactly 8 digits
  if (number.length !== 8) {
    return i18n.t("validations.invalidBankCode");
  }

  return undefined;
};

export const isPostalCode = (value: unknown) =>
  typeof value === "string" && !/^[0-9]{5}$/.test(value) ? i18n.t("validation.isPostalCode") : undefined;

export const validatePasswords = ({
  currentPassword,
  newPassword,
  confirmPassword,
}: PasswordDetailsT): string | boolean | undefined => {
  if (!currentPassword || !newPassword || !confirmPassword) return i18n.t("validation.passwordMissing");
  return (
    validateCurrentPassword(currentPassword) ||
    validateNewPassword(newPassword) ||
    validateConfirmPassword(newPassword)(confirmPassword)
  );
};
const isSameAs = (newPassword: string | null) => (passwordConfirm?: string) =>
  !passwordConfirm || newPassword === passwordConfirm ? undefined : i18n.t("validation.isSame");

export const validateCurrentPassword = (password: string) => evaluate(password)([isMaxLength(50)]);
export const validateNewPassword = (password: string) => {
  return evaluate(password)([isMaxLength(50), isPasswordFormat]);
};
export const validateConfirmPassword = (newPassword?: string) => (confirmPassword: string) =>
  evaluate(confirmPassword)([isSameAs(newPassword ?? null)]);

export const isStreetNumber = (value?: string) => {
  const maxLength10 = lengthIsLessThan(10)(value);
  if (maxLength10) return maxLength10;

  const hasAtLeastOneDigit = value && /\d/.test(value);
  if (!hasAtLeastOneDigit) {
    return i18n.t("validations.invalidHousenumber");
  }

  const specialChars = value && !/^[A-Za-z0-9-/]*$/.test(value) ? i18n.t("validations.isStreetNumber") : undefined;
  const max2charsInRow = value && /[a-zA-Z-/]{3}/.test(value) ? i18n.t("validations.invalidHousenumber") : undefined;

  return specialChars || max2charsInRow || undefined;
};

export const isYearlyConsumption = (value?: string | number | unknown) => {
  return numbersOnly(value);
};

export const isTaxId = (value?: string) => {
  const maxLength15 = lengthIsLessThan(15)(value);
  if (maxLength15) return maxLength15;

  return numbersOnly(value);
};

export const isCompanyName = (value?: string) => {
  return value && !/^[A-Za-z0-9&+-@ ]*$/.test(value) ? i18n.t("validations.invalidCompanyName") : undefined;
};

export const isCustomerNumber = (value?: string) => {
  const minLength4 = lengthIsMoreThan(4)(value);
  if (minLength4) return minLength4;

  const maxLength30 = lengthIsLessThan(30)(value);
  if (maxLength30) return maxLength30;

  const minOneDigit = atLeastOneDigit(value);
  if (minOneDigit) return minOneDigit;

  return value && !/^[A-Za-z0-9-/]*$/.test(value) ? i18n.t("validations.invalidCustomerNumber") : undefined;
};

export const isCounterNumber = (value?: string) => {
  const minLength4 = lengthIsMoreThan(4)(value);
  if (minLength4) return minLength4;

  const maxLength30 = lengthIsLessThan(30)(value);
  if (maxLength30) return maxLength30;

  const minOneDigit = atLeastOneDigit(value);
  if (minOneDigit) return minOneDigit;

  return value && !/^[A-Za-z0-9-/]*$/.test(value) ? i18n.t("validations.invalidCounterNumber") : undefined;
};

export const isDate = (year: string, month: string, day: string) => {
  const result = DateTime.fromISO(`${year}-${month}-${day}`);
  if (!result.isValid) {
    return i18n.t("validations.invalidDate");
  }
  return undefined;
};

const isNewDate = (date: string) => (toDate()(date) ? undefined : i18n.t("validation.isDate"));

export const isValidDay = (day: string) => {
  if (day.length === 1 && /^[0123]$/.test(day)) {
    return true;
  } else if (day.length === 2 && /^(0[123456789])|((1|2)[0123456789])|(3[01])$/.test(day)) {
    return true;
  }
  return false;
};

export const isValidMonth = (month: string) => {
  if (month.length === 1 && /^[01]$/.test(month)) {
    return true;
  } else if (month.length === 2 && /^(0[123456789])|(1[012])$/.test(month)) {
    return true;
  }
  return false;
};

export const isValidYear = (year: string) => {
  if (year.length === 1 && /^[12]$/.test(year)) {
    return true;
  } else if (year.length === 2 && /^(19)|(20)$/.test(year)) {
    return true;
  } else if (year.length === 3 && /^(19)|(20)[0123456789]$/.test(year)) {
    return true;
  } else if (year.length === 4 && /^(19)|(20)[0123456789]{2}$/.test(year)) {
    return true;
  }
  return false;
};

export const isPhoneNumberValid = (value: string) => {
  if (value && typeof value === "string") {
    if (value.length < 6) return i18n.t("validations.invalidPhonenumberLength");
    try {
      return isValidPhoneNumber(value, "DE") ? undefined : i18n.t("validation.isPhoneValid");
    } catch (error) {
      return i18n.t("validations.invalidPhonenumber");
    }
  }
  return undefined;
};

export const isNotFutureDate = (year: string, month: string, day: string) => {
  const result = DateTime.fromISO(`${year}-${month}-${day}`);
  if (result.isValid && result > DateTime.local()) {
    return undefined;
  }
  return i18n.t("validations.hasToBeFutureDate");
};

export const isNotSame = (value1: string | number, value2: string | number, error?: string) => {
  return value1 !== value2 ? undefined : error || i18n.t("validations.shouldBeDifferent");
};

export const is18orOlder = (year: string, month: string, day: string) => {
  const result = DateTime.fromISO(`${year}-${month}-${day}`);
  if (result.isValid && result.diffNow("years").years <= -18) {
    return undefined;
  }
  return i18n.t("validations.is18orOlder");
};

export const isIbanValid = async (iban: string) => {
  if (!iban || iban.length < 15 || iban.length > 31) return i18n.t("validations.invalidIban");

  const result: IbanRespone = await validateIban(iban).then((res) => res.data);
  if (typeof result === "object" && result.ibanValid) {
    return undefined;
  }

  return i18n.t("validations.invalidIban");
};

export const isMalo = (value: string) => {
  if (numbersOnly(value) || isLength(value, 11)) {
    return i18n.t("validations.invalidMalo");
  }
  return undefined;
};

export const isSameIban = (value1: string, value2: string) => {
  if (!!value1 && !!value2 && value1 !== value2) {
    return i18n.t("validations.notSameIban");
  }
  return undefined;
};

export default {
  validateDateOfBirth,
  required,
  requiredDOB,
  lengthIsLessThan,
  lengthIsMoreThan,
  numbersOnly,
  isInteger,
  isNumberSmallerThan,
  atLeastOneDigit,
  isEmail,
  isPassword,
  isConfirmedPassword,
  isPostalCode,
  isStreetNumber,
  isYearlyConsumption,
  isTaxId,
  isCompanyName,
  isCustomerNumber,
  isCounterNumber,
  isDate,
  isValidDay,
  isValidMonth,
  isValidYear,
  isPhoneNumberValid,
  isNotSame,
  isNotFutureDate,
  noSpecialChars,
  is18orOlder,
  validateName,
  isIbanValid,
  isLength,
  isMalo,
  isSameIban,
};
